import type { ReactElement } from 'react';
import Svg, { Path } from 'react-native-svg';

const TRAINER_ICON_VIEWBOX = '0 0 17 19';
const FAN_ICON_VIEWBOX = '0 0 36 36';
const MONGODB_OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i;

type IconProps = {
  width: number;
  height: number;
};

type IconAspect = {
  width: number;
  height: number;
};

const CUSTOM_SPOT_TYPE_ID_PREFIXES = ['custom-'] as const;
const CUSTOM_SPOT_TYPE_ID_SUFFIXES = ['-id', '-type'] as const;

type SystemSpotTypeKey = 'trainer' | 'fan';

const TRAINER_SPOT_TYPE_ID: SystemSpotTypeKey = 'trainer';
const FAN_SPOT_TYPE_ID: SystemSpotTypeKey = 'fan';

function parseViewBoxAspect(viewBox: string): IconAspect {
  const [, , width, height] = viewBox.split(' ');
  const parsedWidth = Number(width);
  const parsedHeight = Number(height);
  if (!Number.isFinite(parsedWidth) || !Number.isFinite(parsedHeight) || parsedWidth <= 0) {
    return { width: 1, height: 1 };
  }
  return { width: parsedWidth, height: parsedHeight };
}

const TRAINER_ICON_ASPECT = parseViewBoxAspect(TRAINER_ICON_VIEWBOX);
const FAN_ICON_ASPECT = parseViewBoxAspect(FAN_ICON_VIEWBOX);

function TrainerSpotIcon({ width, height }: IconProps): ReactElement {
  return (
    <Svg width={width} height={height} viewBox={TRAINER_ICON_VIEWBOX} fill="none">
      <Path
        d="M5.32049 4.72933C5.32049 3.09623 6.64322 1.7735 8.27632 1.7735C9.90941 1.7735 11.2321 3.09623 11.2321 4.72933C11.2321 6.36242 9.90941 7.68515 8.27632 7.68515C6.64322 7.68515 5.32049 6.36242 5.32049 4.72933ZM13.0056 4.72933C13.0056 2.11711 10.8885 0 8.27632 0C5.66411 0 3.54699 2.11711 3.54699 4.72933C3.54699 7.34154 5.66411 9.45865 8.27632 9.45865C10.8885 9.45865 13.0056 7.34154 13.0056 4.72933ZM1.7735 17.735C1.7735 15.1228 3.89061 13.0056 6.50282 13.0056H10.0498C12.662 13.0056 14.7791 15.1228 14.7791 17.735V18.0306C14.7791 18.522 15.1745 18.9173 15.6659 18.9173C16.1573 18.9173 16.5526 18.522 16.5526 18.0306V17.735C16.5526 14.1436 13.6411 11.2321 10.0498 11.2321H6.50282C2.91149 11.2321 0 14.1436 0 17.735V18.0306C0 18.522 0.395342 18.9173 0.886748 18.9173C1.37815 18.9173 1.7735 18.522 1.7735 18.0306V17.735Z"
        fill="#FFFFFF"
      />
    </Svg>
  );
}

function FanSpotIcon({ width, height }: IconProps): ReactElement {
  return (
    <Svg width={width} height={height} viewBox={FAN_ICON_VIEWBOX} fill="none">
      <Path
        d="M20.3496 12.7803C20.3498 13.7947 21.3367 14.4503 22.2422 14.166C22.8902 13.9624 23.5831 13.8516 24.2998 13.8516C27.9639 13.8517 30.9582 16.7326 31.1406 20.3516H23.2207C22.2064 20.3518 21.5508 21.3387 21.835 22.2441C22.0386 22.8922 22.1494 23.5851 22.1494 24.3018C22.1493 27.9659 19.2685 30.9603 15.6494 31.1426V23.2227C15.6491 22.2715 14.7815 21.6359 13.9268 21.7949L13.7568 21.8369C13.1088 22.0405 12.4159 22.1514 11.6992 22.1514C8.03519 22.1512 5.04067 19.2704 4.8584 15.6514H12.7783C13.7928 15.6512 14.4485 14.6643 14.1641 13.7588C13.9605 13.1108 13.8496 12.4179 13.8496 11.7012C13.8498 8.03719 16.7307 5.04172 20.3496 4.85938V12.7803Z"
        stroke="#FFFFFF"
        strokeWidth={2.5}
      />
      <Path
        d="M19.7992 17.9982C19.7992 18.9939 18.9948 19.7982 17.9992 19.7982C17.0036 19.7982 16.1992 18.9939 16.1992 17.9982C16.1992 17.0026 17.0036 16.1982 17.9992 16.1982C18.9948 16.1982 19.7992 17.0026 19.7992 17.9982Z"
        fill="#FFFFFF"
      />
    </Svg>
  );
}

function normalizeSpotTypeToken(value: string): string {
  return value.trim().toLowerCase();
}

function isExplicitCustomSpotTypeId(spotTypeId: string): boolean {
  const id = normalizeSpotTypeToken(spotTypeId);
  if (CUSTOM_SPOT_TYPE_ID_PREFIXES.some((prefix) => id.startsWith(prefix))) return true;
  if (CUSTOM_SPOT_TYPE_ID_SUFFIXES.some((suffix) => id.endsWith(suffix))) return true;
  return false;
}

function isStoredSystemSpotTypeId(spotTypeId: string): boolean {
  return MONGODB_OBJECT_ID_PATTERN.test(spotTypeId.trim());
}

const SYSTEM_SPOT_TYPE_PREFIX: Record<SystemSpotTypeKey, string> = {
  [TRAINER_SPOT_TYPE_ID]: 'tr',
  [FAN_SPOT_TYPE_ID]: 'fn'
};

function matchesSystemSpotTypeName(
  spotTypeId: string,
  spotTypeName: string | undefined,
  systemName: SystemSpotTypeKey
): boolean {
  if (normalizeSpotTypeToken(spotTypeName ?? '') !== systemName) return false;
  if (isExplicitCustomSpotTypeId(spotTypeId)) return false;
  const id = normalizeSpotTypeToken(spotTypeId);
  return id === systemName || isStoredSystemSpotTypeId(spotTypeId);
}

function matchesSystemSpotTypePrefix(
  spotTypeId: string,
  spotTypePrefix: string | undefined,
  spotTypeName: string | undefined,
  systemName: SystemSpotTypeKey
): boolean {
  if (!isStoredSystemSpotTypeId(spotTypeId)) return false;
  if (isExplicitCustomSpotTypeId(spotTypeId)) return false;
  if (normalizeSpotTypeToken(spotTypePrefix ?? '') !== SYSTEM_SPOT_TYPE_PREFIX[systemName]) {
    return false;
  }
  const normalizedName = normalizeSpotTypeToken(spotTypeName ?? '');
  if (normalizedName === '') return true;
  return normalizedName === systemName;
}

function isFanSpotType(
  spotTypeId: string,
  spotTypeName?: string,
  spotTypePrefix?: string
): boolean {
  if (normalizeSpotTypeToken(spotTypeId) === FAN_SPOT_TYPE_ID) return true;
  if (matchesSystemSpotTypePrefix(spotTypeId, spotTypePrefix, spotTypeName, FAN_SPOT_TYPE_ID)) {
    return true;
  }
  return matchesSystemSpotTypeName(spotTypeId, spotTypeName, FAN_SPOT_TYPE_ID);
}

function isTrainerSpotType(
  spotTypeId: string,
  spotTypeName?: string,
  spotTypePrefix?: string
): boolean {
  if (normalizeSpotTypeToken(spotTypeId) === TRAINER_SPOT_TYPE_ID) return true;
  if (matchesSystemSpotTypePrefix(spotTypeId, spotTypePrefix, spotTypeName, TRAINER_SPOT_TYPE_ID)) {
    return true;
  }
  return matchesSystemSpotTypeName(spotTypeId, spotTypeName, TRAINER_SPOT_TYPE_ID);
}

export function resolveSystemSpotTypeKey(
  spotTypeId: string,
  spotTypeName?: string,
  spotTypePrefix?: string
): SystemSpotTypeKey | null {
  if (isFanSpotType(spotTypeId, spotTypeName, spotTypePrefix)) return FAN_SPOT_TYPE_ID;
  if (isTrainerSpotType(spotTypeId, spotTypeName, spotTypePrefix)) return TRAINER_SPOT_TYPE_ID;
  return null;
}

export function isSystemIconSpotTypeId(
  spotTypeId: string,
  spotTypeName?: string,
  spotTypePrefix?: string
): boolean {
  return resolveSystemSpotTypeKey(spotTypeId, spotTypeName, spotTypePrefix) !== null;
}

function hasCustomSpotTypePrefix(spotTypePrefix: string | undefined): boolean {
  return typeof spotTypePrefix === 'string' && spotTypePrefix.trim() !== '';
}

export function shouldRenderNonBookableSpotTypeIcon(
  spotTypeId: string,
  spotTypePrefix: string | undefined,
  spotTypeName?: string,
  useCustomNonBookablePrefixes = false
): boolean {
  if (
    !useCustomNonBookablePrefixes &&
    hasCustomSpotTypePrefix(spotTypePrefix) &&
    resolveSystemSpotTypeKey(spotTypeId, spotTypeName, spotTypePrefix) === null
  ) {
    return true;
  }
  if (resolveSystemSpotTypeKey(spotTypeId, spotTypeName, spotTypePrefix) !== null) {
    return true;
  }
  if (hasCustomSpotTypePrefix(spotTypePrefix)) {
    return false;
  }
  if (spotTypeName?.trim()) {
    return false;
  }
  return true;
}

function getNonBookableSpotIconAspect(
  spotTypeId: string,
  spotTypeName?: string,
  spotTypePrefix?: string
): IconAspect {
  if (isFanSpotType(spotTypeId, spotTypeName, spotTypePrefix)) {
    return FAN_ICON_ASPECT;
  }
  return TRAINER_ICON_ASPECT;
}

export function getNonBookableSpotIconAspectRatio(
  spotTypeId: string,
  spotTypeName?: string,
  spotTypePrefix?: string
): number {
  const { width, height } = getNonBookableSpotIconAspect(spotTypeId, spotTypeName, spotTypePrefix);
  return height / width;
}

export function getNonBookableSpotIcon(
  spotTypeId: string,
  size: { width: number; height: number },
  spotTypeName?: string,
  spotTypePrefix?: string
): ReactElement {
  if (isFanSpotType(spotTypeId, spotTypeName, spotTypePrefix)) {
    return <FanSpotIcon width={size.width} height={size.height} />;
  }
  return <TrainerSpotIcon width={size.width} height={size.height} />;
}

export function trimSpotTypePrefix(spotTypePrefix: string | undefined): string | null {
  const trimmed = spotTypePrefix?.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed;
}
